import { useCallback, useRef } from 'react';
import { Alert } from '../lib/alert.web';
import { useNavigation, useRouter } from 'expo-router';
import { usePreventRemove } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

export function useDirtyGuard(
  isDirty: boolean,
  onSave: () => Promise<boolean | void> | boolean | void,
) {
  const navigation = useNavigation();
  const router = useRouter();
  const { t } = useTranslation('common');
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  // Set by `leave()`: the user already decided to drop this draft (B02:
  // "Discard my change" in a CV conflict), so asking again would be a
  // second prompt for the same answer.
  const bypassRef = useRef(false);

  usePreventRemove(isDirty, ({ data }) => {
    if (bypassRef.current) {
      navigation.dispatch(data.action);
      return;
    }
    Alert.alert(
      t('dirtyGuard.title'),
      t('dirtyGuard.message'),
      [
        { text: t('dirtyGuard.stay'), style: 'cancel' },
        { text: t('dirtyGuard.discard'), style: 'destructive', onPress: () => navigation.dispatch(data.action) },
        {
          text: t('dirtyGuard.save'),
          onPress: async () => {
            try {
              const saved = await onSaveRef.current();
              if (saved !== false) navigation.dispatch(data.action);
            } catch {
              // Save failed; error already toasted by the form; stay on screen.
            }
          },
        },
      ],
      { cancelable: true },
    );
  });

  /**
   * Close this screen without the unsaved-changes prompt, the way the form
   * closes after a successful save.
   */
  const leave = useCallback(() => {
    bypassRef.current = true;
    if (router.canGoBack()) router.back();
  }, [router]);

  return { leave };
}
